import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Btn } from "../components/Btn";
import { Icon } from "../components/Icon";
import { Skeleton } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { ProductCard } from "../components/ProductCard";
import { supabase } from "../lib/supabase";
import { gradientForId } from "../lib/gradients";
import { parseTs } from "../lib/dateUtils";

// Live store page. Fetches products + reviews for this seller_id — no mock feed.
export function StoreDetail({ store, user, onBack, onOpenProduct, onOpenMessages }) {
  const [tab, setTab] = useState("all");
  const [g1, g2] = gradientForId(store.id);
  const queryClient = useQueryClient();

  // One bundle for all the public store data — products, reviews, counts.
  // staleTime 2 min: marketplace listings don't change minute-to-minute.
  const { data: storeData, isLoading: loading } = useQuery({
    queryKey: ["store-detail", store.id],
    queryFn: async () => {
      const [
        { data: prods },
        { data: revs },
        { count: productCount },
        { count: orderCount },
        { data: ratingAgg },
        { count: followerCount },
      ] = await Promise.all([
        supabase
          .from("products")
          .select("id, title, price, compare_price, images, seller_id, category_id, stock_qty, created_at, sellers(store_name, address), category:categories(name, icon)")
          .eq("seller_id", store.id)
          .eq("status", "active")
          .is("deleted_at", null)
          .order("created_at", { ascending: false })
          .limit(40),
        supabase
          .from("reviews")
          .select("id, rating, text, created_at, buyer_id, users(name, avatar_url)")
          .eq("seller_id", store.id)
          .order("created_at", { ascending: false })
          .limit(20),
        supabase
          .from("products")
          .select("id", { count: "exact", head: true })
          .eq("seller_id", store.id)
          .eq("status", "active")
          .is("deleted_at", null),
        supabase
          .from("sub_orders")
          .select("id", { count: "exact", head: true })
          .eq("seller_id", store.id)
          .neq("status", "pending_payment"),
        supabase
          .from("reviews")
          .select("rating")
          .eq("seller_id", store.id),
        supabase
          .from("follows")
          .select("id", { count: "exact", head: true })
          .eq("seller_id", store.id),
      ]);
      const ratings = ratingAgg || [];
      const ratingAvg = ratings.length
        ? ratings.reduce((s, r) => s + (r.rating || 0), 0) / ratings.length
        : 0;
      return {
        products: prods || [],
        reviews: revs || [],
        stats: {
          productCount: productCount ?? 0,
          orderCount: orderCount ?? 0,
          reviewCount: ratings.length,
          ratingAvg,
          followerCount: followerCount ?? 0,
        },
      };
    },
    staleTime: 2 * 60_000,
  });

  const products = storeData?.products || [];
  const reviews = storeData?.reviews || [];
  const liveStats = storeData?.stats || {
    productCount: null,
    orderCount: null,
    reviewCount: null,
    ratingAvg: null,
    followerCount: null,
  };

  // Per-user follow state — separate query so it can refetch independently.
  const { data: followRow } = useQuery({
    queryKey: ["follow-status", store.id, user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("follows")
        .select("id")
        .eq("follower_id", user.id)
        .eq("seller_id", store.id)
        .maybeSingle();
      return data || null;
    },
  });
  const following = !!followRow;

  const toggleFollowMutation = useMutation({
    mutationFn: async () => {
      if (following) {
        const { error } = await supabase
          .from("follows")
          .delete()
          .eq("follower_id", user.id)
          .eq("seller_id", store.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("follows")
          .insert({ follower_id: user.id, seller_id: store.id });
        if (error) throw error;
      }
    },
    onMutate: async () => {
      // Optimistic: flip follow state + bump count immediately.
      await queryClient.cancelQueries({ queryKey: ["follow-status", store.id, user?.id] });
      const wasFollowing = following;
      queryClient.setQueryData(
        ["follow-status", store.id, user?.id],
        wasFollowing ? null : { id: "optimistic" }
      );
      queryClient.setQueryData(["store-detail", store.id], (old) =>
        old
          ? {
              ...old,
              stats: {
                ...old.stats,
                followerCount: (old.stats.followerCount || 0) + (wasFollowing ? -1 : 1),
              },
            }
          : old
      );
      return { wasFollowing };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back on failure.
      queryClient.setQueryData(
        ["follow-status", store.id, user?.id],
        ctx.wasFollowing ? { id: "rollback" } : null
      );
      queryClient.setQueryData(["store-detail", store.id], (old) =>
        old
          ? {
              ...old,
              stats: {
                ...old.stats,
                followerCount: (old.stats.followerCount || 0) + (ctx.wasFollowing ? 1 : -1),
              },
            }
          : old
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["follow-status", store.id, user?.id] });
      queryClient.invalidateQueries({ queryKey: ["store-detail", store.id] });
    },
  });

  function toggleFollow() {
    if (!user?.id || toggleFollowMutation.isPending) return;
    toggleFollowMutation.mutate();
  }

  const newDrops = useMemo(() => {
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    return products.filter((p) => parseTs(p.created_at).getTime() > cutoff);
  }, [products]);
  const onSale = useMemo(
    () => products.filter((p) => p.compare_price && p.compare_price > p.price),
    [products]
  );

  const visible =
    tab === "new" ? newDrops : tab === "sale" ? onSale : tab === "reviews" || tab === "about" ? [] : products;

  return (
    <div>
      <button
        onClick={onBack}
        style={{
          border: "none",
          background: "transparent",
          color: C.hunter,
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          marginBottom: 14,
          padding: 0,
        }}
      >
        <Icon name="chevron-left" size={14} color={C.hunter} />
        Back to stores
      </button>

      <Card pad={0} style={{ marginBottom: 18 }}>
        <div
          style={{
            height: 240,
            background: store.cover_url
              ? `url(${store.cover_url}) center/cover`
              : `linear-gradient(135deg, ${g1}, ${g2})`,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15) 0, transparent 50%), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.1) 0, transparent 40%)",
            }}
          />
        </div>
        <div style={{ padding: "20px 22px", display: "flex", alignItems: "flex-start", gap: 16 }}>
          <div
            style={{
              width: 88,
              height: 88,
              borderRadius: 20,
              background: store.logo_url
                ? `url(${store.logo_url}) center/cover ${C.white}`
                : `linear-gradient(135deg, ${g1}, ${g2})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 34,
              fontWeight: 900,
              color: C.white,
              border: "4px solid " + C.white,
              marginTop: -64,
              boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
              position: "relative",
              zIndex: 2,
              flexShrink: 0,
            }}
          >
            {!store.logo_url && store.store_name?.[0]?.toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h1 style={{ fontSize: 24, fontWeight: 900, margin: 0, letterSpacing: "-0.5px" }}>{store.store_name}</h1>
              {store.status === "active" && (
                <span
                  style={{
                    padding: "2px 7px",
                    borderRadius: 5,
                    background: C.greenBg,
                    color: C.greenDk,
                    fontSize: 9.5,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                  }}
                >
                  VERIFIED
                </span>
              )}
            </div>
            {store.description && (
              <p style={{ fontSize: 13, color: C.slate, margin: "4px 0 8px" }}>{store.description}</p>
            )}
            <div style={{ display: "flex", gap: 14, fontSize: 11.5, color: C.muted, fontWeight: 700, flexWrap: "wrap" }}>
              <span>
                ⭐ {(liveStats.ratingAvg ?? 0).toFixed(1)} ({liveStats.reviewCount ?? 0} reviews)
              </span>
              <span>·</span>
              <span>{liveStats.productCount ?? 0} products</span>
              <span>·</span>
              <span>{liveStats.orderCount ?? 0} orders</span>
              <span>·</span>
              <span>{liveStats.followerCount ?? 0} followers</span>
              {store.address && (
                <>
                  <span>·</span>
                  <span>📍 {store.address}</span>
                </>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn
              kind="outline"
              size="md"
              icon="message-square"
              onClick={() => onOpenMessages?.(store.id)}
            >
              Message
            </Btn>
            <Btn
              kind={following ? "outline" : "primary"}
              size="md"
              icon="heart"
              onClick={toggleFollow}
              disabled={!user?.id || toggleFollowMutation.isPending}
            >
              {following ? "Following" : "Follow"}
              {liveStats.followerCount != null && ` · ${liveStats.followerCount}`}
            </Btn>
          </div>
        </div>
      </Card>

      <div style={{ display: "flex", gap: 2, borderBottom: "1px solid rgba(196,193,183,0.35)", marginBottom: 18 }}>
        {[
          ["all", "All items", products.length],
          ["new", "New drops", newDrops.length],
          ["sale", "On sale", onSale.length],
          ["reviews", "Reviews", liveStats.reviewCount ?? 0],
          ["about", "About"],
        ].map(([k, l, n]) => {
          const on = tab === k;
          return (
            <button
              key={k}
              onClick={() => setTab(k)}
              style={{
                border: "none",
                background: "transparent",
                padding: "10px 16px",
                fontSize: 13,
                fontWeight: on ? 800 : 600,
                color: on ? C.hunter : C.muted,
                borderBottom: on ? `2px solid ${C.hunter}` : "2px solid transparent",
                marginBottom: -1,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {l}
              {n != null && (
                <span style={{ marginLeft: 6, fontSize: 10, color: C.muted, fontWeight: 600 }}>{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} h={220} r={16} />
          ))}
        </div>
      ) : tab === "reviews" ? (
        <ReviewsList reviews={reviews} />
      ) : tab === "about" ? (
        <AboutPanel store={store} />
      ) : visible.length === 0 ? (
        <EmptyState icon="package" title="Nothing here yet" body="Check back soon for new items." />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {visible.map((p) => (
            <ProductCard key={p.id} product={p} onOpen={onOpenProduct} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewsList({ reviews }) {
  if (!reviews.length) {
    return <EmptyState icon="star" title="No reviews yet" body="Reviews from buyers will appear here." />;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {reviews.map((r) => (
        <Card key={r.id}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: r.users?.avatar_url ? `url(${r.users.avatar_url}) center/cover` : C.hunter,
                color: C.dust,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 800,
              }}
            >
              {!r.users?.avatar_url && (r.users?.name?.[0] || "?").toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800 }}>{r.users?.name || "Buyer"}</div>
              <div style={{ fontSize: 11, color: C.muted }}>
                {"⭐".repeat(r.rating)} · {parseTs(r.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>
          {r.text && <p style={{ fontSize: 13, color: C.slate, margin: 0, lineHeight: 1.5 }}>{r.text}</p>}
        </Card>
      ))}
    </div>
  );
}

const DELIVERY_LABELS = {
  self: { label: "Self delivery", icon: "truck" },
  pickup: { label: "In-store pickup", icon: "map-pin" },
  courier: { label: "Courier service", icon: "package" },
};

function AboutPanel({ store }) {
  const opts = Array.isArray(store.delivery_options) ? store.delivery_options : [];
  return (
    <Card>
      <h3 style={{ fontSize: 15, fontWeight: 900, margin: "0 0 10px" }}>About {store.store_name}</h3>
      <p style={{ fontSize: 13, color: C.slate, lineHeight: 1.6, margin: "0 0 14px" }}>
        {store.description || "This seller has not added a description yet."}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14, marginBottom: 14 }}>
        <InfoLine label="Category" value={store.category || "—"} />
        <InfoLine label="Location" value={store.address || "Not specified"} />
        <InfoLine label="Commission" value={`${store.commission_rate || 10}%`} />
      </div>
      <div>
        <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
          Delivery options
        </div>
        {opts.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.muted, fontWeight: 600 }}>
            Seller hasn't set delivery options yet.
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {opts.map((id) => {
              const meta = DELIVERY_LABELS[id] || { label: id, icon: "truck" };
              return (
                <div
                  key={id}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    borderRadius: 999,
                    background: C.greenBg,
                    color: C.hunter,
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  <Icon name={meta.icon} size={12} color={C.hunter} />
                  {meta.label}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

function InfoLine({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}
